import { useCallback, useMemo } from 'react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';

export interface EntityRefreshData {
  allowRefresh: boolean;
  canRefresh: boolean;
  refreshEntity: () => Promise<void>;
}

export const useEntityRefresh = (
  entity: Entity,
  entityLocation?: string,
): EntityRefreshData => {
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);

  const allowRefresh = useMemo(() => {
    return (
      entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:')
    );
  }, [entityLocation]);

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Component refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      if (e instanceof Error) {
        errorApi.post(e);
      } else {
        errorApi.post(
          new Error('Unknown error occurred during entity refresh'),
        );
      }
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  return {
    allowRefresh: allowRefresh ?? false,
    canRefresh,
    refreshEntity,
  };
};
